import { Component, Input, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { ECElementEvent } from 'echarts';

@Component({
  selector: 'app-histogram',
  templateUrl: './histogram.component.html',
  styleUrls: ['./histogram.component.css']
})
export class HistogramComponent implements OnInit {
  @Input() histogramData: any;
  @Input() markline: any;
  @Output() chartClick = new Subject<ECElementEvent>();
  @Input() barCount: number;
  @Input() barWidth: number;
  @Input() barName: string;
  @Input() barName2: string;
  @Input() isMock: boolean;
  @Input() binCountType: string = "binCount";
  chartOption: any;

  // Cached values to prevent recalculations
  private cachedData: any[] = [];
  private cachedBins: any[] = [];
  private cachedBarWidth: number;
  private cachedBarCount: number;
  private cachedBinCountType: string;

  ngOnInit() { }

  ngOnChanges(): void {
    if (this.histogramData?.length) {
      this._plotHistogram();
    }
  }

  /**
   * Handles chart click events
   * @param event Click event
   */
  onChartClick(event: any) {
    if (!this.isMock) {
      this.chartClick.next(event);
    }
  }

  /**
   * Finds bin for value using binary search for better performance
   * @param value Value to find bin for
   * @returns Matching bin or null
   */
  _findBinForValue(value) {
    if (!this.cachedBins?.length) return null;

    let left = 0;
    let right = this.cachedBins.length - 1;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const bin = this.cachedBins[mid];

      if (value >= bin.range[0] && value <= bin.range[1]) {
        return bin;
      }

      if (value < bin.range[0]) {
        right = mid - 1;
      } else {
        left = mid + 1;
      }
    }

    return null;
  }

  private _plotHistogram() {
    const data = this.histogramData;

    // Check if recalculation is necessary by comparing with cached values
    const needsRecalculation =
      this.cachedData !== data ||
      this.cachedBarWidth !== this.barWidth ||
      this.cachedBarCount !== this.barCount ||
      this.cachedBinCountType !== this.binCountType;

    // Only recalculate bins if needed
    if (needsRecalculation) {
      this.cachedData = data;
      this.cachedBarWidth = this.barWidth;
      this.cachedBarCount = this.barCount;
      this.cachedBinCountType = this.binCountType;

      // Calculate histogram bins based on type
      this.cachedBins = this.binCountType === 'binCount'
        ? this._calculateHistogramBins(data)
        : this._calculateHistogramBinsWithWidth(data, this.barWidth);
    }

    // Create chart options
    this._createChartOptions();
  }

  /**
   * Creates chart options
   */
  private _createChartOptions() {
    // Prepare markline data acc to marker options
    const marklineData = this._prepareMarklineData();

    this.chartOption = {
      title: {
        text: this.histogramData?.[0]?.uom ? `${this.histogramData?.[0]?.title} (${this.histogramData?.[0]?.uom})` : this.histogramData?.[0]?.title,
        top: 0,
        textStyle: {
          fontSize: 12,
          fontWeight: 'bold'
        }
      },
      grid: {
        left: '10%',
        right: '7%',
        bottom: '12%',
        top: '8%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: this.cachedBins?.map(bin => bin.label),
        axisLabel: {
          interval: 'auto',
          rotate: -15,
          fontSize: 8,
          fontWeight: 'bold'
        },
        axisTick: { show: false },
        name: this.barName,
        nameLocation: "middle",
        nameGap: 50
      },
      yAxis: {
        type: 'value',
        name: this.barName2,
        nameLocation: "middle",
        nameGap: 45,
        axisLabel: {
          fontWeight: 'bold'
        }
      },
      toolbox: {
        right: '10%',
        show: true,
        feature: {
          dataZoom: {
            show: true,
            icon: {
              back: ' '
            }
          },
          restore: {}
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function (params) {
          const param = params[0];
          return `${param.name}<br/>Count: ${param.value}`;
        }
      },
      series: [
        {
          name: 'Histogram',
          type: 'bar',
          barWidth: '25%',
          data: this.cachedBins?.map(bin => ({
            value: bin.count,
            binValues: bin.binValues,
            binEntities: bin.binEntities,
            binLabel: bin.label
          })),
          emphasis: {
            disabled: true,
          },
          itemStyle: {
            color: this.histogramData?.[0]?.color,
            borderRadius: [5, 5, 0, 0]
          },
          large: true,
          label: {
            show: true,
            position: 'top',
            fontWeight: 'bold',
            formatter: (params) => {
              return params.value > 0 ? params.value : '';
            }
          },
          markLine: {
            symbol: ['none', 'none'],
            data: marklineData
          }
        },
      ],
    };
  }

  /**
   * Prepares markline data
   */
  private _prepareMarklineData() {
    const marklineData = [];

    // Add min x-axis marker if needed
    if (this.markline?.xAxis?.markers?.min !== undefined) {
      const minBin = this._findBinForValue(this.markline.xAxis.markers.min);
      if (minBin) {
        marklineData.push({
          name: 'Min Value',
          xAxis: minBin.label,
          lineStyle: {
            color: '#ff0000',
            type: 'solid',
            width: 1
          },
          label: {
            formatter: `Min: ${this.markline.xAxis.markers.min}`,
            position: 'insideEndTop',
            fontSize: 12,
            fontWeight: 'bold',
            backgroundColor: 'rgba(255,255,255,0.7)',
            padding: [2, 4]
          }
        });
      }
    }

    // Add max x-axis marker if needed
    if (this.markline?.xAxis?.markers?.max !== undefined) {
      const maxBin = this._findBinForValue(this.markline.xAxis.markers.max);
      if (maxBin) {
        marklineData.push({
          name: 'Max Value',
          xAxis: maxBin.label,
          lineStyle: {
            color: '#ff0000',
            type: 'solid',
            width: 1
          },
          label: {
            formatter: `Max: ${this.markline.xAxis.markers.max}`,
            position: 'insideEndTop',
            fontSize: 12,
            fontWeight: 'bold',
            backgroundColor: 'rgba(255,255,255,0.7)',
            padding: [2, 4]
          }
        });
      }
    }

    // Add y-axis markers
    if (this.markline?.yAxis?.markers?.max) {
      marklineData.push({ yAxis: this.markline.yAxis.markers.max });
    }

    if (this.markline?.yAxis?.markers?.min) {
      marklineData.push({ yAxis: this.markline.yAxis.markers.min });
    }

    return marklineData;
  }

  private _calculateHistogramBins(data: any[]): { label: string; count: number; range: [number, number]; binValues: any, binEntities: any }[] {
    const dataValues = data?.map(({ value }) => value);
    const max = Math.max(...dataValues);
    const min = Math.min(...dataValues);
    let binCount: any;
    if ([...new Set(dataValues)]?.length === 1) {
      binCount = 1;
    } else {
      binCount = this.barCount
    }
    // Automatically determine binCount based on the max & min of data points
    binCount = binCount === 0 ? 1 : binCount;
    const binWidth = (max - min) / binCount;

    const bins: { label: string; count: number; range: [number, number], binValues: any, binEntities: any }[] = [];

    for (let i = 0; i < binCount; i++) {
      const binStart = min + i * binWidth;
      const binEnd = binStart + binWidth;

      const label = binStart === binEnd ? `${binStart.toFixed(data?.[0]?.roundOf)}` : `${binStart.toFixed(data?.[0]?.roundOf)} - ${binEnd.toFixed(data?.[0]?.roundOf)}`;
      const count = dataValues.filter(value => value >= binStart && value <= binEnd).length;
      const binValues = dataValues.filter(value => value >= binStart && value <= binEnd);
      const binEntities = data.filter(item => item.value >= binStart && item.value <= binEnd)?.map(item => {
        item.range = label;
        return item;
      });

      bins.push({ label, count, range: [binStart, binEnd], binValues, binEntities });
    }

    return bins;
  }

  /**
   * Calculates histogram bins with width
   * @param data 
   * @param [binSize] 
   * @returns histogram bins with width 
   */
  private _calculateHistogramBinsWithWidth(data: any[], binSize: number = 10): {
    label: string;
    count: number;
    range: [number, number];
    binValues: any;
    binEntities: any;
  }[] {
    if (!data?.length) return [];

    // Process only positive data (excluding negative values)
    const dataValues = data?.map(item => Number(item.value))?.filter(value => value >= 0)?.map(value => Math.floor(value));
    const min = Math.floor(Math.min(...dataValues) / binSize) * binSize;
    const max = Math.ceil(Math.max(...dataValues) / binSize) * binSize;

    const binMap = new Map();

    for (let binStart = min; binStart < max; binStart += binSize) {
      const binEnd = binStart + binSize;
      const key = `${binStart}-${binEnd}`;
      binMap?.set(key, {
        start: binStart,
        end: binEnd,
        count: 0,
        indices: []
      });
    }

    dataValues?.forEach((value, index) => {
      const binStart = Math.floor(value / binSize) * binSize;
      const binEnd = binStart + binSize;
      const key = `${binStart}-${binEnd}`;

      if (binMap?.has(key)) {
        binMap.get(key).count++;
        binMap.get(key)?.indices?.push(index);
      }
    });

    // Convert map to array of bins
    const bins = [];
    binMap?.forEach((bin, key) => {
      // Only process bin data if there are values in this bin
      if (bin.count > 0) {
        const isLastBin = bin.end >= max;
        const label = `${bin.start} - ${bin.end}`;

        const binValues = bin.indices?.map(i => dataValues[i]);
        const binEntities = bin.indices?.map(i => {
          const item = { ...data[i] };
          item.range = label;
          return item;
        });

        bins?.push({
          label,
          count: bin.count,
          range: [bin.start, bin.end],
          binValues,
          binEntities
        });
      }
    });

    return bins.sort((a, b) => a.range[0] - b.range[0]);
  }
}